/**
 * NewRequestPage
 * 
 * Página para criar nova requisição de compra.
 * Permite adicionar itens dinamicamente e encaminhar para revisão técnica.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Package, Plus, Trash2,
  ArrowLeft, Send, Loader2, Sparkles,
  UserCheck, ShoppingCart,
  Building2, CheckCircle2, X, Tag, Search
} from 'lucide-react';
import { supplierService, productService, brandService } from '../../../services/erpApi';
import { purchaseService } from '../../../services/purchasesApi';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../../components';
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal';
import ERPProductSearch, { type Product } from '../../../components/ERP/ProductSearch';
import PriorityDropdown from './PriorityDropdown';
import { getAvailableUnitsFromProduct, getEffectiveMinStock } from '../../../utils/stock';
import './NewRequestPage.css';

interface RequisitionItem {
  product_id: number | null;
  product_name: string;
  requested_qty: number;
  priority: 'normal' | 'urgent' | 'critical';
  min_stock?: number;
  current_stock?: number;
}

interface ConfirmModalState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: (() => void) | null;
  type: 'warning' | 'danger' | 'info';
}

const NewRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { toast, showToast, hideToast } = useToast();
  const [items, setItems] = useState<RequisitionItem[]>([]);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [requisitionId, setRequisitionId] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });
  
  // Produto selecionado para adicionar
  const [selectedProduct, setSelectedProduct] = useState<(Product & { min_stock?: number }) | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const supplierDropdownRef = useRef<HTMLDivElement>(null);

  // Título da requisição
  const [title, setTitle] = useState<string>('');

  // Informações adicionais
  const [additionalInfo, setAdditionalInfo] = useState<string>('');

  // Estados do fornecedor
  const [showSupplierModal, setShowSupplierModal] = useState<boolean>(false);
  const [supplierData, setSupplierData] = useState<{ name: string; cnpj: string }>({ name: '', cnpj: '' });
  const [searchingSupplier, setSearchingSupplier] = useState<boolean>(false);
  const [foundSupplier, setFoundSupplier] = useState<any>(null);
  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [supplierName, setSupplierName] = useState<string>('');
  const [supplierSearchResults, setSupplierSearchResults] = useState<any[]>([]);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState<boolean>(false);
  const [searchingSupplierByName, setSearchingSupplierByName] = useState<boolean>(false);

  // Estados do modal de marcas
  const [showBrandModal, setShowBrandModal] = useState<boolean>(false);
  const [brands, setBrands] = useState<any[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<number[]>([]);
  const [loadingBrands, setLoadingBrands] = useState<boolean>(false);
  const [brandSearchTerm, setBrandSearchTerm] = useState<string>('');



  // Carregar marcas
  const loadBrands = useCallback(async (): Promise<void> => {
    setLoadingBrands(true);
    try {
      const response = await brandService.getAll({ active: true });
      if (response.success && response.data) {
        setBrands(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
      showToast('Erro ao carregar marcas', 'error');
    } finally {
      setLoadingBrands(false);
    }
  }, [showToast]);

  // Carregar marcas quando o modal abrir
  useEffect(() => {
    if (showBrandModal) {
      loadBrands();
    }
  }, [showBrandModal, loadBrands]);

  // Fechar dropdown de fornecedores ao clicar fora
  useEffect(() => {
    if (!showSupplierModal) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const clickedInsideSupplierInput = target.closest('input[type="text"]') && 
        target.getAttribute('placeholder')?.includes('Nome completo ou razão social');
      const clickedInsideSupplierDropdown = supplierDropdownRef.current?.contains(target);
      
      if (!clickedInsideSupplierInput && !clickedInsideSupplierDropdown) {
        setShowSupplierDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSupplierModal]);

  // Gerar requisição automática baseada em estoque mínimo
  const handleGenerateAutoRequisition = async (): Promise<void> => {
    if (selectedBrands.length === 0) {
      showToast('Selecione pelo menos uma marca', 'warning');
      return;
    }

    try {
      setLoadingBrands(true);
      
      // Buscar todos os produtos ativos
      const response = await productService.getAll({ 
        active: true 
      });
      
      if (!response.success || !response.data) {
        showToast('Erro ao buscar produtos', 'error');
        return;
      }

      // Filtrar produtos das marcas selecionadas
      const allProducts: Product[] = response.data.filter((product: any) => {
        // Verificar se o produto tem brand_id e está na lista de marcas selecionadas
        const productBrandId = product.brand_id || product.brand;
        return productBrandId && selectedBrands.includes(Number(productBrandId));
      });

      // Filtrar produtos que estão abaixo do estoque mínimo
      const productsBelowMin: RequisitionItem[] = [];
      
      for (const product of allProducts) {
        // Buscar informações completas do produto para obter min_stock e stock_quantity
        try {
          const productResponse = await productService.getById(product.id);
          if (productResponse.success && productResponse.data) {
            const fullProduct = productResponse.data;
            const minStock = getEffectiveMinStock(fullProduct.min_stock);
            const currentStock = getAvailableUnitsFromProduct(fullProduct);
            
            if (minStock > 0 && currentStock < minStock) {
              const quantityNeeded = minStock - currentStock;
              
              // Verificar se o produto já está na lista
              const existingItem = items.find(item => item.product_id === product.id);
              if (!existingItem && quantityNeeded > 0) {
                productsBelowMin.push({
                  product_id: product.id,
                  product_name: product.name,
                  requested_qty: quantityNeeded,
                  priority: 'normal',
                  min_stock: minStock,
                  current_stock: currentStock
                });
              }
            }
          }
        } catch (error) {
          console.error(`Erro ao buscar detalhes do produto ${product.id}:`, error);
        }
      }

      if (productsBelowMin.length === 0) {
        showToast('Nenhum produto abaixo do estoque mínimo encontrado para as marcas selecionadas', 'info');
        setShowBrandModal(false);
        return;
      }

      // Adicionar produtos à lista
      setItems([...items, ...productsBelowMin]);
      showToast(`${productsBelowMin.length} produto(s) adicionado(s) automaticamente`, 'success');
      setShowBrandModal(false);
      setSelectedBrands([]);
    } catch (error) {
      console.error('Erro ao gerar requisição automática:', error);
      showToast('Erro ao gerar requisição automática', 'error');
    } finally {
      setLoadingBrands(false);
    }
  };


  // Carregar dados do rascunho quando houver ID na URL
  useEffect(() => {
    const loadDraft = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const response = await purchaseService.getById(id);
        
        if (response.success && response.data) {
          const requisition = response.data;
          
          // Carregar título
          if (requisition.title) {
            setTitle(requisition.title);
          }
          
          // Carregar informações adicionais
          if (requisition.additional_info) {
            setAdditionalInfo(requisition.additional_info);
          }
          
          // Carregar fornecedor
          if (requisition.supplier_id) {
            setSupplierId(requisition.supplier_id);
            if (requisition.supplier) {
              setSupplierName(requisition.supplier.name || '');
              setSupplierData({
                name: requisition.supplier.name || '',
                cnpj: requisition.supplier.cpf_cnpj || ''
              });
            } else if (requisition.supplier_name) {
              setSupplierName(requisition.supplier_name);
              setSupplierData({
                name: requisition.supplier_name,
                cnpj: requisition.supplier_cpf_cnpj || ''
              });
            }
          }
          
          // Carregar itens
          if (requisition.items && Array.isArray(requisition.items) && requisition.items.length > 0) {
            const loadedItems: RequisitionItem[] = requisition.items.map((item: any) => ({
              product_id: item.product_id || null,
              product_name: item.product_name || '',
              requested_qty: item.requested_qty || 1,
              priority: (item.priority && ['normal', 'urgent', 'critical'].includes(item.priority)) 
                ? item.priority 
                : 'normal',
              min_stock: item.min_stock !== null && item.min_stock !== undefined ? Number(item.min_stock) : undefined,
              current_stock: item.current_stock !== null && item.current_stock !== undefined ? Number(item.current_stock) : undefined
            }));
            setItems(loadedItems);
          }
        } else {
          showToast('Rascunho não encontrado', 'warning');
          navigate('/erp/purchases');
        }
      } catch (error: any) {
        console.error('Erro ao carregar rascunho:', error);
        showToast(error.response?.data?.message || 'Erro ao carregar rascunho', 'error');
        navigate('/erp/purchases');
      } finally {
        setIsLoading(false);
      }
    };

    loadDraft();
  }, [id, navigate, showToast]);

  // Formatar CNPJ/CPF
  const formatCnpj = (value: string): string => {
    const clean = value.replace(/\D/g, '');
    if (clean.length <= 11) {
      return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4').replace(/(\d{3})(\d{3})(\d)/, '$1.$2.$3').replace(/(\d{3})(\d)/, '$1-$2');
    } else {
      return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5').replace(/(\d{2})(\d{3})(\d{3})/, '$1.$2.$3').replace(/(\d{2})(\d{3})/, '$1.$2');
    }
  };

  // Buscar fornecedor por CNPJ
  const handleCnpjChange = async (cnpj: string) => {
    const cleanCnpj = cnpj.replace(/\D/g, '');
    const formatted = formatCnpj(cnpj);
    setSupplierData({ ...supplierData, cnpj: formatted });

    if (cleanCnpj.length >= 11) {
      setSearchingSupplier(true);
      setFoundSupplier(null);
      
      try {
        const response = await supplierService.findByCnpj(cleanCnpj);
        if (response.success && response.data) {
          setFoundSupplier(response.data);
          setSupplierData({
            name: response.data.name,
            cnpj: formatted
          });
          showToast('Fornecedor encontrado e vinculado automaticamente', 'success');
        } else {
          setFoundSupplier(null);
        }
      } catch (err) {
        console.error('Erro ao buscar fornecedor:', err);
        setFoundSupplier(null);
      } finally {
        setSearchingSupplier(false);
      }
    } else {
      setFoundSupplier(null);
    }
  };

  // Buscar fornecedores por nome
  useEffect(() => {
    const trimmedName = supplierData.name.trim();
    
    if (trimmedName.length >= 2 && !foundSupplier) {
      setShowSupplierDropdown(true);
      setSearchingSupplierByName(true);
      
      const timeoutId = setTimeout(async () => {
        try {
          const response = await supplierService.getAll({ 
            search: trimmedName,
            limit: 10
          });
          if (response.success && response.data) {
            setSupplierSearchResults(response.data);
          } else {
            setSupplierSearchResults([]);
          }
        } catch (err) {
          console.error('Erro ao buscar fornecedores:', err);
          setSupplierSearchResults([]);
        } finally {
          setSearchingSupplierByName(false);
        }
      }, 300);

      return () => {
        clearTimeout(timeoutId);
        setSearchingSupplierByName(false);
      };
    } else {
      setSupplierSearchResults([]);
      setShowSupplierDropdown(false);
      setSearchingSupplierByName(false);
    }
  }, [supplierData.name, foundSupplier]);

  // Salvar fornecedor
  const handleSaveSupplier = async (): Promise<void> => {
    if (!supplierData.name) {
      showToast('Nome do fornecedor é obrigatório', 'error');
      return;
    }

    try {
      let finalSupplierId = foundSupplier?.id;
      
      if (!finalSupplierId) {
        const createResponse = await supplierService.create({
          name: supplierData.name,
          cpfCnpj: supplierData.cnpj.replace(/\D/g, '')
        });
        if (createResponse.success && createResponse.data?.id) {
          finalSupplierId = createResponse.data.id;
        } else {
          showToast(createResponse.message || 'Erro ao criar fornecedor', 'error');
          return;
        }
      }

      if (!finalSupplierId) {
        showToast('Erro: ID do fornecedor não encontrado', 'error');
        return;
      }

      setSupplierId(finalSupplierId);
      setSupplierName(supplierData.name);
      showToast(
        foundSupplier 
          ? 'Fornecedor vinculado com sucesso' 
          : 'Fornecedor cadastrado e vinculado com sucesso',
        'success'
      );
      setShowSupplierModal(false);
    } catch (error: any) {
      console.error('Erro ao salvar fornecedor:', error);
      showToast(error.message || 'Erro ao salvar fornecedor', 'error');
    }
  };

  // Selecionar produto do ERPProductSearch
  const handleSelectProduct = (product: Product): void => {
    // Buscar informações completas do produto para obter min_stock e stock_quantity
    productService.getById(product.id).then((response) => {
      if (response.success && response.data) {
        const fullProduct = {
          ...product,
          min_stock: response.data.min_stock,
          stock_quantity: response.data.stock_quantity !== null && response.data.stock_quantity !== undefined 
            ? Number(response.data.stock_quantity) 
            : product.stock_quantity
        };
        setSelectedProduct(fullProduct as Product & { min_stock?: number });
      } else {
        setSelectedProduct(product);
      }
    }).catch(() => {
      setSelectedProduct(product);
    });
    setQuantity(1);
  };

  // Adicionar item à lista
  const handleAddItem = (): void => {
    if (!selectedProduct) return;

    // Verificar se o produto já está na lista
    const existingItem = items.find(item => item.product_id === selectedProduct.id);
    if (existingItem) {
      showToast('Este produto já está na lista', 'warning');
      return;
    }

    const newItem: RequisitionItem = {
      product_id: selectedProduct.id,
      product_name: selectedProduct.name,
      requested_qty: quantity,
      priority: 'normal',
      min_stock: selectedProduct.min_stock,
      current_stock: selectedProduct.stock_quantity !== null && selectedProduct.stock_quantity !== undefined 
        ? Number(selectedProduct.stock_quantity) 
        : undefined
    };

    setItems([...items, newItem]);
    setSelectedProduct(null);
    setQuantity(1);
  };

  const handleRemoveItem = (idx: number): void => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleItemChange = (idx: number, field: keyof RequisitionItem, value: string | number | 'normal' | 'urgent' | 'critical'): void => {
    setItems(prev => prev.map((item, i) => 
      i === idx ? { ...item, [field]: value } : item
    ));
  };



  const handleSaveDraft = async (): Promise<void> => {
    if (!title.trim()) {
      showToast('Por favor, informe um título para a requisição', 'warning');
      return;
    }

    if (items.length === 0) {
      showToast('Adicione pelo menos um item à requisição', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      const requisitionData: any = {
        title: title.trim(),
        priority: 'normal',
        status: 'draft',
        items: items.map(item => {
          // Garantir que priority sempre tenha um valor válido
          let priorityValue = 'normal';
          if (item.priority) {
            if (['normal', 'urgent', 'critical'].includes(item.priority)) {
              priorityValue = item.priority;
            }
          }
          
          return {
            product_id: item.product_id,
            product_name: item.product_name,
            requested_qty: parseFloat(String(item.requested_qty)) || 1,
            priority: priorityValue
          };
        })
      };

      // Adicionar supplier_id se houver
      if (supplierId) {
        requisitionData.supplier_id = supplierId;
      }

      // Adicionar informações adicionais se houver
      if (additionalInfo.trim()) {
        requisitionData.additional_info = additionalInfo.trim();
      }

      let response;
      if (id) {
        // Atualizar rascunho existente
        response = await purchaseService.update(id, requisitionData);
      } else {
        // Criar novo rascunho
        response = await purchaseService.create(requisitionData);
      }
      
      if (response.success) {
        showToast('Rascunho salvo com sucesso!', 'success');
        setTimeout(() => {
          navigate('/erp/purchases');
        }, 1500);
      } else {
        showToast(response.message || 'Erro ao salvar rascunho', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao salvar rascunho:', error);
      showToast(error.response?.data?.message || 'Erro ao salvar rascunho', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendToReview = async (): Promise<void> => {
    if (!title.trim()) {
      showToast('Por favor, informe um título para a requisição', 'warning');
      return;
    }

    if (items.length === 0) {
      showToast('Adicione pelo menos um item à requisição', 'warning');
      return;
    }

    // Validar itens
    const invalidItems = items.filter(item => !item.product_name || !item.requested_qty || item.requested_qty <= 0);
    if (invalidItems.length > 0) {
      showToast('Preencha todos os campos dos itens corretamente', 'warning');
      return;
    }

    setIsSending(true);
    try {
      const requisitionData: any = {
        title: title.trim(),
        priority: 'normal',
        status: 'technical_review',
        items: items.map(item => {
          // Garantir que priority sempre tenha um valor válido
          let priorityValue = 'normal';
          if (item.priority) {
            if (['normal', 'urgent', 'critical'].includes(item.priority)) {
              priorityValue = item.priority;
            }
          }
          
          return {
            product_id: item.product_id,
            product_name: item.product_name,
            requested_qty: parseFloat(String(item.requested_qty)) || 1,
            priority: priorityValue
          };
        })
      };

      // Adicionar supplier_id se houver
      if (supplierId) {
        requisitionData.supplier_id = supplierId;
      }

      // Adicionar informações adicionais se houver
      if (additionalInfo.trim()) {
        requisitionData.additional_info = additionalInfo.trim();
      }

      let response;
      if (id) {
        // Atualizar rascunho existente e mudar status
        response = await purchaseService.update(id, requisitionData);
      } else {
        // Criar nova requisição
        response = await purchaseService.create(requisitionData);
      }
      
      if (response.success) {
        setRequisitionId(response.data?.id || id || `RQ-${Date.now()}`);
        setShowSuccess(true);
        setTimeout(() => {
          navigate('/erp/purchases');
        }, 3000);
      } else {
        showToast(response.message || 'Erro ao encaminhar requisição', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao encaminhar requisição:', error);
      showToast(error.response?.data?.message || 'Erro ao encaminhar requisição', 'error');
    } finally {
      setIsSending(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="new-request-success">
        <div className="success-icon">
          <UserCheck size={48} />
        </div>
        <h2>Encaminhado!</h2>
        <p>Sua requisição foi enviada para Revisão Técnica.</p>
        {requisitionId && (
          <div className="success-id">{requisitionId}</div>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="new-request-page">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', flexDirection: 'column', gap: '16px' }}>
          <Loader2 size={32} className="spinner" />
          <p>Carregando rascunho...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="new-request-page">
      <button 
        className="btn-back-header"
        onClick={() => {
          if (items.length > 0 || title.trim()) {
            setConfirmModal({
              isOpen: true,
              title: 'Descartar Rascunho',
              message: 'Tem certeza que deseja descartar este rascunho? Todas as alterações serão perdidas.',
              type: 'warning',
              onConfirm: () => {
                navigate('/erp/purchases');
              }
            });
          } else {
            navigate('/erp/purchases');
          }
        }}
        type="button"
      >
        <ArrowLeft size={20} />
      </button>

      <div className="request-form-card">
        <span className="status-badge-draft">Status: Rascunho</span>
        <div className="form-header">
          <div className="header-icon">
            <ShoppingCart size={28} />
          </div>
          <div>
            <h1>Montar Requisição</h1>
            <p>Identifique a falta e solicite ao setor técnico</p>
          </div>
        </div>

        {/* Título da Requisição */}
        <label className="form-label">Título da Requisição</label>
        <input
          type="text"
          className="new-request-input"
          placeholder="Ex: Reposição de Insumos UTI"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        {/* Fornecedor e Seleção de Marca */}
        <div style={{ marginTop: '1.5rem' }}>
          <label className="form-label">Fornecedor</label>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
            {/* Campo do Fornecedor */}
            <div style={{ flex: 1 }}>
              {supplierName ? (
                <div style={{
                  padding: '0.75rem 1rem',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--border-radius-xl)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: 'var(--text-primary)'
                }}>
                  <Building2 size={18} style={{ color: 'var(--color-primary)' }} />
                  <span style={{ fontWeight: 'var(--font-weight-bold)' }}>{supplierName}</span>
                </div>
              ) : (
                <div style={{
                  padding: '0.75rem 1rem',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--border-radius-xl)',
                  color: 'var(--text-tertiary)',
                  fontStyle: 'italic'
                }}>
                  Nenhum fornecedor selecionado
                </div>
              )}
            </div>
            
            {/* Botões */}
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => {
                  if (supplierId) {
                    setSupplierData({
                      name: supplierName,
                      cnpj: ''
                    });
                  } else {
                    setSupplierData({ name: '', cnpj: '' });
                  }
                  setFoundSupplier(null);
                  setSupplierSearchResults([]);
                  setShowSupplierDropdown(false);
                  setShowSupplierModal(true);
                }}
                className="new-request-add-item-btn"
              >
                <Building2 size={18} />
                {supplierName ? 'Alterar' : 'Adicionar'}
              </button>
              {supplierName && (
                <button
                  type="button"
                  onClick={() => {
                    setSupplierId(null);
                    setSupplierName('');
                    setSupplierData({ name: '', cnpj: '' });
                    setFoundSupplier(null);
                    setSupplierSearchResults([]);
                    setShowSupplierDropdown(false);
                  }}
                  style={{
                    padding: '0.75rem',
                    background: 'var(--color-error-light)',
                    color: 'var(--color-error)',
                    border: 'none',
                    borderRadius: 'var(--border-radius-xl)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Remover fornecedor"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
          
          {/* Botão de Selecionar Marca */}
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <button
              type="button"
              onClick={() => {
                setShowBrandModal(true);
                setSelectedBrands([]);
                setBrandSearchTerm('');
              }}
              className="new-request-add-item-btn"
            >
              <Tag size={18} />
              Selecionar Marca
            </button>
          </div>
        </div>

        {/* Informações Adicionais */}
        <div style={{ marginTop: '1.5rem' }}>
          <label className="form-label">
            Informações Adicionais <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-tertiary)', fontWeight: 'var(--font-weight-normal)' }}>(Opcional)</span>
          </label>
          <textarea
            className="new-request-textarea"
            placeholder="Ex: Solicitei 200 acima do estoque mínimo pois temos uma licitação vencida"
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            rows={3}
            style={{
              resize: 'vertical',
              minHeight: '80px',
              fontFamily: 'inherit'
            }}
          />
        </div>

        {/* Lista de Itens */}
        <div className="items-section">
          {/* Busca de produtos */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">Buscar Produto / Insumo</label>
            <ERPProductSearch 
              onSelect={handleSelectProduct}
              placeholder="Buscar produto por nome, SKU, código de barras..."
            />
          </div>

          {/* Formulário de adicionar item */}
          {selectedProduct && (
            <div className="new-request-add-item-card">
              <div className="new-request-add-item-header">
                <div className="new-request-add-item-info">
                  <div className="new-request-add-item-image">
                    {selectedProduct.image_url ? (
                      <img src={selectedProduct.image_url} alt={selectedProduct.name} />
                    ) : (
                      <Package size={24} />
                    )}
                  </div>
                  <div className="new-request-add-item-details">
                    <p className="new-request-add-item-name">
                      {selectedProduct.name}
                    </p>
                    {selectedProduct.sku && (
                      <p className="new-request-add-item-sku">
                        SKU: {selectedProduct.sku}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedProduct(null);
                    setQuantity(1);
                  }}
                  className="new-request-add-item-close"
                >
                  <X size={18} />
                </button>
              </div>
              
              <div className="new-request-add-item-actions">
                <div className="new-request-add-item-stock-info">
                  <div className="new-request-add-item-stock-item">
                    <label className="new-request-add-item-label">
                      Estoque Mínimo
                    </label>
                    <div className="new-request-add-item-stock-value">
                      {selectedProduct.min_stock !== null && selectedProduct.min_stock !== undefined 
                        ? selectedProduct.min_stock 
                        : '—'}
                    </div>
                  </div>
                  <div className="new-request-add-item-stock-item">
                    <label className="new-request-add-item-label">
                      Estoque Atual
                    </label>
                    <div className="new-request-add-item-stock-value">
                      {selectedProduct.stock_quantity !== null && selectedProduct.stock_quantity !== undefined 
                        ? selectedProduct.stock_quantity 
                        : '—'}
                    </div>
                  </div>
                </div>
                <div className="new-request-add-item-quantity">
                  <label className="new-request-add-item-label">
                    Quantidade
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseFloat(e.target.value) || 1))}
                    className="new-request-add-item-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="new-request-add-item-btn"
                >
                  <Plus size={18} />
                  Adicionar
                </button>
              </div>
            </div>
          )}

          {/* Lista de itens adicionados */}
          {items.length === 0 ? (
            <div className="empty-items-state">
              <Package size={48} />
              <p>Nenhum item na lista de requisição</p>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', marginTop: '0.5rem', fontWeight: 'var(--font-weight-normal)' }}>
                Busque e adicione produtos acima
              </p>
            </div>
          ) : (
            <div className="items-list">
              {items.map((item, idx) => (
                <div key={idx} className="item-row">
                  <div className="item-field product-field">
                    <label>Produto / Insumo</label>
                    <div style={{
                      padding: '0.75rem 1rem',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--border-radius-xl)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: 'var(--text-primary)'
                    }}>
                      <Package size={16} style={{ color: 'var(--color-primary)' }} />
                      <span style={{ fontWeight: 'var(--font-weight-bold)' }}>{item.product_name}</span>
                    </div>
                  </div>
                  <div className="item-field quantity-field">
                    <label>Quantidade</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="0"
                      value={item.requested_qty}
                      onChange={(e) => handleItemChange(idx, 'requested_qty', parseFloat(e.target.value) || 1)}
                      className="quantity-input"
                    />
                  </div>
                  <div className="item-field min-stock-field">
                    <label>Estoque Mínimo</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="—"
                      value={item.min_stock !== null && item.min_stock !== undefined ? item.min_stock : ''}
                      readOnly
                      className="min-stock-input"
                    />
                  </div>
                  <div className="item-field current-stock-field">
                    <label>Estoque Atual</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="—"
                      value={item.current_stock !== null && item.current_stock !== undefined ? item.current_stock : ''}
                      readOnly
                      className="current-stock-input"
                    />
                  </div>
                  <div className="item-field priority-field">
                    <label>Prioridade</label>
                    <PriorityDropdown
                      value={item.priority}
                      onChange={(value) => handleItemChange(idx, 'priority', value)}
                    />
                  </div>
                  <div className="item-actions">
                    <button 
                      className="btn-remove-item"
                      onClick={() => handleRemoveItem(idx)}
                      type="button"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="form-footer">
          <div className="info-box">
            <Sparkles size={20} />
            <p>O Gemini revisará as quantidades baseado no estoque real antes de liberar a cotação.</p>
          </div>
          <div className="footer-actions">
            <button
              className="btn-save-draft"
              onClick={handleSaveDraft}
              disabled={isSaving || isSending}
              type="button"
            >
              {isSaving ? <Loader2 size={18} className="spinner" /> : 'Salvar Rascunho'}
            </button>
            <button
              className="btn-send-review"
              onClick={handleSendToReview}
              disabled={items.length === 0 || isSending || isSaving}
              type="button"
            >
              {isSending ? <Loader2 size={18} className="spinner" /> : <Send size={18} />}
              Encaminhar para Revisão
            </button>
          </div>
        </div>
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' })}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' });
        }}
        confirmText="Confirmar"
        cancelText="Cancelar"
      />

      {/* Modal de Fornecedor */}
      {showSupplierModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.5rem',
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            background: 'var(--bg-primary)',
            width: '100%',
            maxWidth: '36rem',
            borderRadius: '1.5rem',
            padding: '2.5rem',
            position: 'relative',
            boxShadow: 'var(--shadow-2xl)',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: 'var(--border-radius-xl)',
                  background: 'rgba(99, 102, 241, 0.1)',
                  color: 'rgb(99, 102, 241)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Building2 size={24} />
                </div>
                <div>
                  <h3 style={{
                    fontSize: 'var(--font-size-xl)',
                    fontWeight: 'var(--font-weight-black)',
                    color: 'var(--text-primary)',
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--letter-spacing-tight)',
                    margin: 0
                  }}>Fornecedor</h3>
                  <p style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-secondary)',
                    margin: 0,
                    marginTop: '0.25rem'
                  }}>Cadastrar ou Vincular Fornecedor</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowSupplierModal(false);
                  setSupplierData({ name: '', cnpj: '' });
                  setFoundSupplier(null);
                  setSupplierSearchResults([]);
                  setShowSupplierDropdown(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-tertiary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.5rem',
                  borderRadius: 'var(--border-radius-lg)',
                  transition: 'color var(--transition-base)'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {foundSupplier && (
                <div style={{
                  padding: '1rem',
                  marginBottom: '0.5rem',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid #10b981',
                  borderRadius: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#10b981'
                }}>
                  <CheckCircle2 size={16} />
                  <strong style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-bold)' }}>Fornecedor encontrado:</strong>
                  <span style={{ fontSize: 'var(--font-size-xs)' }}>{foundSupplier.name}</span>
                </div>
              )}

              <div>
                <label style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-black)',
                  color: 'var(--text-tertiary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginLeft: '0.25rem',
                  display: 'block',
                  marginBottom: '0.5rem'
                }}>CNPJ/CPF</label>
                <input
                  type="text"
                  value={supplierData.cnpj}
                  onChange={(e) => handleCnpjChange(e.target.value)}
                  placeholder="00.000.000/0000-00 ou 000.000.000-00"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: 'var(--bg-secondary)',
                    border: 'none',
                    borderRadius: 'var(--border-radius-xl)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--text-primary)',
                    transition: 'all var(--transition-base)'
                  }}
                />
                {searchingSupplier && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <Loader2 className="spinner" size={14} />
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>
                      Buscando fornecedor...
                    </span>
                  </div>
                )}
              </div>

              <div style={{ position: 'relative' }}>
                <label style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-black)',
                  color: 'var(--text-tertiary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginLeft: '0.25rem',
                  display: 'block',
                  marginBottom: '0.5rem'
                }}>Nome do Fornecedor *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={supplierData.name}
                    onChange={(e) => {
                      setSupplierData({ ...supplierData, name: e.target.value });
                      setFoundSupplier(null);
                    }}
                    placeholder="Nome completo ou razão social"
                    required
                    disabled={!!foundSupplier}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'var(--bg-secondary)',
                      border: 'none',
                      borderRadius: 'var(--border-radius-xl)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-bold)',
                      color: 'var(--text-primary)',
                      transition: 'all var(--transition-base)',
                      opacity: foundSupplier ? 0.7 : 1,
                      cursor: foundSupplier ? 'not-allowed' : 'text'
                    }}
                  />
                  {searchingSupplierByName && (
                    <div style={{
                      position: 'absolute',
                      right: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Loader2 className="spinner" size={14} />
                    </div>
                  )}
                </div>
                {showSupplierDropdown && supplierSearchResults.length > 0 && (
                  <div 
                    ref={supplierDropdownRef}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '0.5rem',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--border-radius-xl)',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
                    }}
                  >
                    {supplierSearchResults.map((supplier) => (
                      <div
                        key={supplier.id}
                        onClick={() => {
                          setSupplierData({
                            name: supplier.name,
                            cnpj: supplier.cpf_cnpj || ''
                          });
                          setFoundSupplier(supplier);
                          setShowSupplierDropdown(false);
                          setSupplierSearchResults([]);
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--border-color-light)',
                          transition: 'all var(--transition-base)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--bg-secondary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <div style={{
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 'var(--font-weight-bold)',
                          color: 'var(--text-primary)',
                          marginBottom: '0.25rem'
                        }}>
                          {supplier.name}
                        </div>
                        {supplier.cpf_cnpj && (
                          <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--text-tertiary)'
                          }}>
                            {formatCnpj(supplier.cpf_cnpj)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '0.75rem',
              marginTop: '2rem',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={() => {
                  setShowSupplierModal(false);
                  setSupplierData({ name: '', cnpj: '' });
                  setFoundSupplier(null);
                  setSupplierSearchResults([]);
                  setShowSupplierDropdown(false);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: 'var(--border-radius-xl)',
                  cursor: 'pointer',
                  fontWeight: 'var(--font-weight-bold)',
                  fontSize: 'var(--font-size-xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  transition: 'all var(--transition-base)'
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveSupplier}
                disabled={!supplierData.name}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: supplierData.name ? 'var(--color-primary)' : 'var(--bg-secondary)',
                  color: supplierData.name ? 'var(--text-inverse)' : 'var(--text-tertiary)',
                  border: 'none',
                  borderRadius: 'var(--border-radius-xl)',
                  cursor: supplierData.name ? 'pointer' : 'not-allowed',
                  fontWeight: 'var(--font-weight-bold)',
                  fontSize: 'var(--font-size-xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  transition: 'all var(--transition-base)',
                  opacity: supplierData.name ? 1 : 0.5
                }}
              >
                {foundSupplier ? 'Vincular' : 'Cadastrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Seleção de Marcas */}
      {showBrandModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.5rem',
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            background: 'var(--bg-primary)',
            width: '100%',
            maxWidth: '42rem',
            borderRadius: '1.5rem',
            padding: '2.5rem',
            position: 'relative',
            boxShadow: 'var(--shadow-2xl)',
            border: '1px solid var(--border-color)',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: 'var(--border-radius-xl)',
                  background: 'rgba(16, 185, 129, 0.1)',
                  color: 'rgb(16, 185, 129)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Tag size={24} />
                </div>
                <div>
                  <h3 style={{
                    fontSize: 'var(--font-size-xl)',
                    fontWeight: 'var(--font-weight-black)',
                    color: 'var(--text-primary)',
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--letter-spacing-tight)',
                    margin: 0
                  }}>Selecionar Marcas</h3>
                  <p style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-secondary)',
                    margin: 0,
                    marginTop: '0.25rem'
                  }}>Gere requisição automática para produtos abaixo do estoque mínimo</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowBrandModal(false);
                  setSelectedBrands([]);
                  setBrandSearchTerm('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-tertiary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.5rem',
                  borderRadius: 'var(--border-radius-lg)',
                  transition: 'color var(--transition-base)'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Busca de Marcas */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Search size={18} style={{
                    position: 'absolute',
                    left: '1rem',
                    color: 'var(--text-tertiary)',
                    pointerEvents: 'none'
                  }} />
                  <input
                    type="text"
                    value={brandSearchTerm}
                    onChange={(e) => setBrandSearchTerm(e.target.value)}
                    placeholder="Buscar marca..."
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem 0.75rem 2.5rem',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--border-radius-xl)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-bold)',
                      color: 'var(--text-primary)',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Lista de Marcas */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                marginBottom: '1.5rem',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--border-radius-xl)',
                background: 'var(--bg-secondary)',
                padding: '0.5rem'
              }}>
                {loadingBrands ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '3rem',
                    flexDirection: 'column',
                    gap: '1rem'
                  }}>
                    <Loader2 className="spinner" size={32} />
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', margin: 0 }}>Carregando marcas...</p>
                  </div>
                ) : (
                  (() => {
                    const filteredBrands = brands.filter(brand =>
                      brand.name?.toLowerCase().includes(brandSearchTerm.toLowerCase())
                    );
                    
                    if (filteredBrands.length === 0) {
                      return (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '3rem',
                          flexDirection: 'column',
                          gap: '1rem'
                        }}>
                          <Tag size={48} style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />
                          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', margin: 0 }}>
                            {brandSearchTerm ? 'Nenhuma marca encontrada' : 'Nenhuma marca disponível'}
                          </p>
                        </div>
                      );
                    }

                    return filteredBrands.map((brand) => {
                      const isSelected = selectedBrands.includes(brand.id);
                      return (
                        <div
                          key={brand.id}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedBrands(selectedBrands.filter(id => id !== brand.id));
                            } else {
                              setSelectedBrands([...selectedBrands, brand.id]);
                            }
                          }}
                          style={{
                            padding: '0.75rem 1rem',
                            marginBottom: '0.5rem',
                            background: isSelected ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-primary)',
                            border: `1px solid ${isSelected ? 'rgb(16, 185, 129)' : 'var(--border-color)'}`,
                            borderRadius: 'var(--border-radius-lg)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'all var(--transition-base)'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = 'var(--bg-secondary)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = 'var(--bg-primary)';
                            }
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: 'var(--border-radius-md)',
                              background: isSelected ? 'rgb(16, 185, 129)' : 'var(--bg-secondary)',
                              color: isSelected ? 'white' : 'var(--text-tertiary)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Tag size={16} />
                            </div>
                            <span style={{
                              fontSize: 'var(--font-size-sm)',
                              fontWeight: 'var(--font-weight-bold)',
                              color: 'var(--text-primary)'
                            }}>
                              {brand.name}
                            </span>
                          </div>
                          {isSelected && (
                            <CheckCircle2 size={20} style={{ color: 'rgb(16, 185, 129)' }} />
                          )}
                        </div>
                      );
                    });
                  })()
                )}
              </div>

              {/* Footer do Modal */}
              <div style={{
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'flex-end',
                paddingTop: '1rem',
                borderTop: '1px solid var(--border-color)'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowBrandModal(false);
                    setSelectedBrands([]);
                    setBrandSearchTerm('');
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    borderRadius: 'var(--border-radius-xl)',
                    cursor: 'pointer',
                    fontWeight: 'var(--font-weight-bold)',
                    fontSize: 'var(--font-size-xs)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    transition: 'all var(--transition-base)'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleGenerateAutoRequisition}
                  disabled={selectedBrands.length === 0 || loadingBrands}
                  className="new-request-add-item-btn"
                  style={{
                    opacity: selectedBrands.length === 0 || loadingBrands ? 0.5 : 1,
                    cursor: selectedBrands.length === 0 || loadingBrands ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loadingBrands ? (
                    <>
                      <Loader2 size={18} className="spinner" />
                      Gerando...
                    </>
                  ) : (
                    <>
                      <Plus size={18} />
                      Gerar Requisição ({selectedBrands.length} marca{selectedBrands.length !== 1 ? 's' : ''})
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewRequestPage;
